import dayjs from "dayjs";
import { useFormik } from "formik";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import MESSAGES from "../../../../common/data/constants/messages";
import { IWorkOrder } from "../../../../common/data/dtos/work-order";
import { IWorkOrderCreator } from "../../../../common/data/dtos/work-order-creator";
import { IWorkOrderFilter } from "../../../../common/data/dtos/work-order-filter";
import { IWorkOrderPaginationFilter } from "../../../../common/data/dtos/work-order-pagination-filter";
import Button from "../../../../components/bootstrap/Button";
import Card, { CardActions, CardBody, CardHeader, CardLabel, CardTitle } from "../../../../components/bootstrap/Card";
import Dropdown, { DropdownMenu, DropdownToggle } from "../../../../components/bootstrap/Dropdown";
import Checks from "../../../../components/bootstrap/forms/Checks";
import FormGroup from "../../../../components/bootstrap/forms/FormGroup";
import Input from "../../../../components/bootstrap/forms/Input";
import Label from "../../../../components/bootstrap/forms/Label";
import Select from "../../../../components/bootstrap/forms/Select";
import Spinner from "../../../../components/bootstrap/Spinner";
import { DownloadWorkOrderReport } from "../../../../components/extras/DownloadWorkOrderReport";
import { MoreActions } from "../../../../components/extras/MoreActions";
import { showSuccess, showWarning } from "../../../../components/extras/Notifications";
import PaginationButtons, { dataPagination, PER_COUNT } from "../../../../components/PaginationButtons";
import useSelectTable from "../../../../hooks/useSelectTable";
import { hiddenMenu } from "../../../../menu";
import employeeService from "../../../../services/employee-service";
import workOrderService from "../../../../services/work-order-service";
import LoadingPage from "../../loading/LoadingPage";

export const CommonWorkOrderList = () => {

    const [currentPage, setCurrentPage] = useState(1);
    const [perPage, setPerPage] = useState(PER_COUNT['10']);

    const [isLoading, setIsLoading] = useState<boolean>(false);

    const [workOrders, setWorkOrders] = useState<IWorkOrder[]>([]);
    const [workOrderCreators, setWorkOrderCreators] = useState<IWorkOrderCreator[]>([]);

    const [filterMenu, setFilterMenu] = useState<boolean>(false);
    const [filteredData, setFilteredData] = useState<IWorkOrder[]>([]);

    const onCurrentPageItems = dataPagination(filteredData, currentPage, perPage);
    const { selectTable, SelectAllCheck } = useSelectTable(onCurrentPageItems);

    const formik = useFormik({
        initialValues: {
            minDate: '',
            maxDate: '',
            employee: '',
            serviceType: ''
        },
        onSubmit: (values: IWorkOrderFilter) => {
            filterData(workOrders, values);
            storePagination(values);
            setFilterMenu(false);
        },
    });

    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {

        setIsLoading(true);

        try {
            const [workOrdersResponse, workOrderCreatorResponse] = await Promise.all([
                workOrderService.getWorkOrders(),
                employeeService.getByWorkOrderCreator()
            ]);

            if (workOrdersResponse.error) {
                showWarning(workOrdersResponse.error);
                return;
            }

            if (workOrderCreatorResponse.error) {
                showWarning(workOrderCreatorResponse.error);
                return;
            }

            setWorkOrders(workOrdersResponse.workOrders || []);
            setWorkOrderCreators(workOrderCreatorResponse.employees || []);

            getStoredPagination(workOrdersResponse.workOrders || []);
            window.history.replaceState({}, document.title);

        } catch (error) {
            showWarning('Erro ao carregar dados');
        } finally {
            setIsLoading(false);
        }
    }

    const filterData = (items: IWorkOrder[], values: IWorkOrderFilter) => {
        if (!values.minDate && !values.maxDate && !values.employee && !values.serviceType) {
            localStorage.removeItem(process.env.STORAGE_FILTER_KEY || '');
            resetPagination();

            return setFilteredData(items);
        }

        let filteredItems = [...items];

        // 1. Filtro de Data Inicial (Mínima)
        if (values.minDate) {
            // Transformamos o valor do Formik para o início do dia
            const min = dayjs(values.minDate).startOf('day');

            filteredItems = filteredItems.filter((workOrder) => {
                if (!workOrder.segmentStartDate) return false;

                const itemDate = dayjs(workOrder.segmentStartDate);

                // A lógica correta: O dia do item é igual ao dia selecionado? 
                // OU o item acontece depois do dia selecionado?
                return itemDate.isSame(min, 'day') || itemDate.isAfter(min, 'day');
            });
        }

        // 2. Filtro de Data Final (Máxima)
        if (values.maxDate) {
            const max = dayjs(values.maxDate).startOf('day');

            filteredItems = filteredItems.filter((workOrder) => {
                if (!workOrder.segmentStartDate) return false;

                const itemDate = dayjs(workOrder.segmentStartDate);

                // O dia do item é igual ao dia selecionado?
                // OU o item acontece antes do dia selecionado?
                return itemDate.isSame(max, 'day') || itemDate.isBefore(max, 'day');
            });
        }

        // 3. Filtro de Funcionário
        if (values.employee) {
            filteredItems = filteredItems.filter((workOrder) =>
                workOrder.createdBy === values.employee
            );
        }

        // 3. Filtro de Tipo de Serviço
        if (values.serviceType) {
            filteredItems = filteredItems.filter((workOrder) =>
                workOrder.serviceType === values.serviceType
            );
        }

        resetPagination();

        return setFilteredData(filteredItems);
    };

    if (isLoading) {
        return <LoadingPage />;
    }

    const handleWorkOrderViewClick = async (workOrder: IWorkOrder) => {
        if (workOrder.id) {
            storePagination(formik.values);
            navigate(`../${hiddenMenu.workOrderDetail.path.replace(':id', workOrder.id)}`);
        }
    };

    const handleDeleteClick = async (workOrder: IWorkOrder) => {
        if (!workOrder.id) {
            showWarning(MESSAGES.ACTION.DELETE_ID_ERROR);
            return;
        }

        setIsLoading(true);

        const response = await workOrderService.deleteWorkOrder(workOrder.id);

        setIsLoading(false);

        if (response.error) {
            showWarning(response.error);
            return;
        }

        showSuccess(MESSAGES.ACTION.DELETED);
        await fetchData();
    };

    const resetPagination = () => {
        setCurrentPage(1);
        setPerPage(10);
    }

    const storePagination = (values: IWorkOrderFilter) => {
        const state: IWorkOrderPaginationFilter = {
            filters: values,
            currentPage: currentPage,
            rowsPerPage: perPage
        };

        localStorage.setItem(process.env.STORAGE_FILTER_KEY || '', JSON.stringify(state));
    }

    const getStoredPagination = (workOrders: IWorkOrder[]) => {
        const storedFilters = localStorage.getItem(process.env.STORAGE_FILTER_KEY || '');
        const isReturningFromDetail = location.state?.returnedFromDetail;

        if (storedFilters && isReturningFromDetail) {
            const parsed = JSON.parse(storedFilters);
            filterData(workOrders || [], parsed.filters);

            formik.setValues({
                minDate: parsed.filters.minDate || '',
                maxDate: parsed.filters.maxDate || '',
                employee: parsed.filters.employee || '',
                serviceType: parsed.filters.serviceType || ''
            });

            setPerPage(parsed.rowsPerPage);
            setCurrentPage(parsed.currentPage);

        } else {
            setFilteredData(workOrders || []);
        }
    }

    const canView = (workOrderIds: string[]) => {
        if (!workOrderIds || workOrderIds.length === 0) {
            showWarning('Não há ordens de serviço selecionadas');
            return false;
        }

        const selectedItems = filteredData.filter(item =>
            item.id && workOrderIds.includes(item.id as string)
        );

        if (selectedItems.length === 0) {
            return false;
        }

        // 1. Pegamos as referências do primeiro item para comparação
        const firstCreator = selectedItems[0].createdBy;
        const firstServiceType = selectedItems[0].serviceType;
        const firstCustomerId = selectedItems[0].customerId; // <- Nova referência

        // 2. Validamos se todos os criadores são iguais
        const isSameCreator = selectedItems.every(item => item.createdBy === firstCreator);
        if (!isSameCreator) {
            showWarning('As ordens de serviço selecionadas devem ser do mesmo responsável');
            return false;
        }

        // 3. Validamos se todos os tipos de serviço são iguais
        const isSameServiceType = selectedItems.every(item => item.serviceType === firstServiceType);
        if (!isSameServiceType) {
            showWarning('As ordens de serviço selecionadas devem ser do mesmo serviço');
            return false;
        }

        // 4. Nova Validação: Garantir que todos os CustomerId são iguais
        const isSameCustomer = selectedItems.every(item => item.customerId === firstCustomerId);
        if (!isSameCustomer) {
            showWarning('As ordens de serviço selecionadas devem ser do mesmo cliente');
            return false;
        }

        return true;
    }

    const handleViewClick = async (workOrderIds: string[]) => {
        if (!canView(workOrderIds)) {
            return;
        }

        if (workOrderIds && workOrderIds.length > 0) {
            storePagination(formik.values);

            // Junta os GUIDs em uma string: "guid1,guid2,guid3"
            const guidString = workOrderIds.join(',');

            const basePath = hiddenMenu.workOrderView.path.replace('/:id', '');
            navigate(`../${basePath}?ids=${guidString}`);
        }
    }

    return (
        <>
            <Card style={{ minHeight: 600 }}>
                <CardHeader borderSize={1}>
                    <CardLabel icon='InsertDriveFile' iconColor='primary'>
                        <CardTitle tag='div' className='h5'>
                            Ordens de Serviço
                        </CardTitle>
                    </CardLabel>
                    <CardActions>

                        <Button color="light" icon="RemoveRedEye" onClick={() => handleViewClick(selectTable.values.selectedList)} isDisable={isLoading}>
                            {isLoading && <Spinner isSmall inButton isGrow />}
                            Visualizar
                        </Button>

                        <DownloadWorkOrderReport workOrderIds={selectTable.values.selectedList} filteredData={filteredData} />

                        <Dropdown isOpen={filterMenu} setIsOpen={setFilterMenu} isButtonGroup>
                            <DropdownToggle hasIcon={false}>
                                <Button icon='Filter' color='light' data-tour='filter'>
                                    Filtrar
                                </Button>
                            </DropdownToggle>
                            <DropdownMenu
                                isAlignmentEnd
                                size='lg'
                                isCloseAfterLeave={false}
                                data-tour='filter-menu'>
                                <div className='container py-2'>
                                    <form className='row g-3' onSubmit={formik.handleSubmit}>
                                        <div className='col-12'>
                                            <FormGroup id='minDate' label='De' isFloating>
                                                <Input type="date" onChange={formik.handleChange} onBlur={formik.handleBlur} value={formik.values.minDate} />
                                            </FormGroup>
                                        </div>
                                        <div className='col-12'>
                                            <FormGroup id='maxDate' label='Até' isFloating>
                                                <Input type="date" onChange={formik.handleChange} onBlur={formik.handleBlur} value={formik.values.maxDate} />
                                            </FormGroup>
                                        </div>
                                        <div className='col-12'>
                                            <FormGroup>
                                                <Label htmlFor='serviceType'>Tipo</Label>
                                                <Select
                                                    id='serviceType'
                                                    ariaLabel='Tipo'
                                                    placeholder='Tipo'
                                                    list={[
                                                        { value: '', text: 'Todos' },
                                                        { value: 'SE', text: 'SE - Subestação' },
                                                        { value: 'LD', text: 'LD - Linha de Distribuição' },
                                                        { value: 'LT', text: 'LT - Linha de Transmissão' },
                                                        { value: 'PCH', text: 'PCH - Pequena Central Hidrelétrica' },
                                                    ]}
                                                    onChange={formik.handleChange}
                                                    value={formik.values.serviceType}
                                                />
                                            </FormGroup>
                                        </div>
                                        <div className='col-12'>
                                            <FormGroup>
                                                <Label htmlFor='employee'>Colaborador</Label>
                                                <Select
                                                    id='employee'
                                                    ariaLabel='Colaborador'
                                                    placeholder='Colaborador'
                                                    list={workOrderCreators.map((workOrderCreator) => ({
                                                        value: workOrderCreator.id,
                                                        text: `${workOrderCreator.name}`,
                                                    }))}
                                                    onChange={formik.handleChange}
                                                    value={formik.values.employee}
                                                />
                                            </FormGroup>
                                        </div>
                                        <div className='col-6'>
                                            <Button
                                                color='primary'
                                                isOutline
                                                className='w-100'
                                                onClick={formik.resetForm}>
                                                Limpar
                                            </Button>
                                        </div>
                                        <div className='col-6'>
                                            <Button color='primary' className='w-100' type='submit'>
                                                Aplicar
                                            </Button>
                                        </div>
                                    </form>
                                </div>
                            </DropdownMenu>
                        </Dropdown>
                    </CardActions>
                </CardHeader>
                <CardBody className='table-responsive'>
                    <table className='table table-modern table-hover'>
                        <thead>
                            <tr>
                                <th scope='col'>{SelectAllCheck}</th>
                                <th>Data</th>
                                <th>Tipo</th>
                                <th>Responsável</th>
                                <th>Cliente</th>
                                <th>Torre</th>
                                <th>Linha</th>
                                <th>Observações</th>
                                <td aria-labelledby='Actions' />
                            </tr>
                        </thead>
                        <tbody>
                            {onCurrentPageItems.map((item: IWorkOrder) => (
                                <tr key={item.id}>
                                    <th scope='row' aria-label='Check'>
                                        <Checks
                                            id={item.id?.toString()}
                                            name={'selectedList'}
                                            value={item.id}
                                            onChange={selectTable.handleChange}
                                            checked={selectTable.values.selectedList.includes(
                                                // @ts-ignore
                                                item.id.toString(),
                                            )}
                                            ariaLabel={'selectedList'}
                                        />
                                    </th>
                                    <td>{item.segmentStartDate ? dayjs(item.segmentStartDate).format('DD/MM/YYYY hh:MM') : '-'}</td>
                                    <td>{item?.serviceType ?? '-'}</td>
                                    <td>{item?.employee?.name ?? '-'}</td>
                                    <td>{item?.customer?.name ?? '-'}</td>
                                    <td>{item.tower == '' || !item.tower ? '-' : item.tower}</td>
                                    <td>{item.lineName == '' || !item.lineName ? '-' : item.lineName}</td>
                                    <td>{item?.notes ?? '-'}</td>

                                    <MoreActions
                                        id={item.id}
                                        showEdit={true}
                                        showDelete={true}
                                        onDeleteClick={() => handleDeleteClick(item)}
                                        onEditClick={() => handleWorkOrderViewClick(item)}
                                    />
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </CardBody>
                {<PaginationButtons data={filteredData} label='items' setCurrentPage={setCurrentPage} currentPage={currentPage} perPage={perPage} setPerPage={setPerPage} />}
            </Card>
        </>
    );

}